/**
 * Émolo Ring Sizer – JS
 * Objetivo:
 * 1) Calibrar pantalla con moneda/tarjeta -> calcular pxPerMm
 * 2) Medir anillo ajustando el círculo -> convertir a mm (diámetro interior)
 * 3) Recomendar la talla Émolo más cercana (según tabla)
 *
 * Compatible con Shopify Theme Editor:
 * - Re-inicializa cuando se recarga la sección en el editor.
 */

(function () {
  "use strict";

  /** Tabla Émolo (DIÁMETRO INTERIOR en mm) según vuestra guía */
  const EMOLO_SIZES = [
    { size: 8,  mm: 15.2, eu: 48, us: 4.5 },
    { size: 11, mm: 16.2, eu: 51, us: 5.5 },
    { size: 12, mm: 16.5, eu: 52, us: 6 },
    { size: 14, mm: 17.2, eu: 54, us: 7 },
    { size: 16, mm: 17.8, eu: 56, us: 8 },
    { size: 18, mm: 18.4, eu: 58, us: 8.5 },
    { size: 20, mm: 19.0, eu: 60, us: 9.5 },
    { size: 22, mm: 20.0, eu: 62, us: 10 }
  ];

  /** Referencias de calibración */
  const REFS = {
    coin_2eur: { type: "circle", mm: 25.75 },
    coin_1eur: { type: "circle", mm: 23.25 },
    coin_50c:  { type: "circle", mm: 24.25 },
    card:      { type: "card",   mm: 85.60 } // ancho tarjeta
  };

  /** Parse seguro de JSON */
  function safeJsonParse(str) {
    try { return JSON.parse(str); } catch (e) { return {}; }
  }

  /** Encuentra la talla más cercana y detecta si está "entre dos tallas" */
  function closestSize(dmm) {
    let best = EMOLO_SIZES[0];
    let bestDiff = Math.abs(dmm - best.mm);

    for (const s of EMOLO_SIZES) {
      const diff = Math.abs(dmm - s.mm);
      if (diff < bestDiff) { best = s; bestDiff = diff; }
    }

    // "Entre dos tallas" si está muy cerca de dos valores
    const sorted = [...EMOLO_SIZES].sort((a,b) => Math.abs(dmm-a.mm) - Math.abs(dmm-b.mm));
    const a = sorted[0], b = sorted[1];
    const between = (Math.abs(dmm - a.mm) <= 0.20 && Math.abs(dmm - b.mm) <= 0.20) ? { a, b } : null;

    return { best, between };
  }

  /** Inicializa una instancia de la sección */
  function initRingSizer(root) {
    if (!root || root.dataset.emoloInit === "1") return;
    root.dataset.emoloInit = "1";

    // i18n (frontend) viene en un <script type="application/json" data-emolo-ring-sizer-i18n>
    const i18nEl = root.querySelector("[data-emolo-ring-sizer-i18n]");
    const i18n = i18nEl ? safeJsonParse(i18nEl.textContent || "{}") : {};

    const sectionId = root.getAttribute("data-section-id");

    // Elementos (ids incluyen sectionId para evitar colisiones)
    const refSelect   = root.querySelector(`#emoloRef-${sectionId}`);
    const calibSlider = root.querySelector(`#emoloCalibSlider-${sectionId}`);
    const ringSlider  = root.querySelector(`#emoloRingSlider-${sectionId}`);
    const shape       = root.querySelector(`#emoloShape-${sectionId}`);
    const ring        = root.querySelector(`#emoloRing-${sectionId}`);
    const hint        = root.querySelector(`#emoloHint-${sectionId}`);
    const calibDone   = root.querySelector(`#emoloCalibDone-${sectionId}`);

    const stepBtns = root.querySelectorAll(".emolo-rs__step");
    const panels   = root.querySelectorAll(".emolo-rs__panel");

    const mmOut    = root.querySelector(`#emoloMm-${sectionId}`);
    const sizeOut  = root.querySelector(`#emoloSize-${sectionId}`);
    const diaOut   = root.querySelector(`#emoloDia-${sectionId}`);
    const equivOut = root.querySelector(`#emoloEquiv-${sectionId}`);

    if (!refSelect || !calibSlider || !ringSlider || !shape || !ring || !calibDone) return;

    let pxPerMm = null;

    function setStep(n) {
      panels.forEach(p => p.classList.toggle("emolo-rs__panel--active", p.getAttribute("data-panel") === String(n)));
      stepBtns.forEach(b => b.classList.toggle("emolo-rs__step--active", b.getAttribute("data-step") === String(n)));
    }

    function disableStep2() {
      stepBtns.forEach(b => { if (b.getAttribute("data-step") === "2") b.disabled = true; });
    }

    function enableStep2() {
      stepBtns.forEach(b => { if (b.getAttribute("data-step") === "2") b.disabled = false; });
    }

    function updateCalibShape() {
      const px = Number(calibSlider.value);
      shape.style.width = px + "px";

      // Si calibración con tarjeta, usamos un rectángulo con ratio aproximado (altura = 0.62 del ancho)
      shape.style.height = (REFS[refSelect.value].type === "card") ? (px * 0.62) + "px" : px + "px";
    }

    function setRefUI() {
      const ref = REFS[refSelect.value];

      if (ref.type === "card") {
        shape.style.borderRadius = "14px";
        if (hint) hint.textContent = i18n.hint_card || "Alinea una tarjeta y ajusta.";
      } else {
        shape.style.borderRadius = "999px";
        if (hint) hint.textContent = i18n.hint_coin || "Coloca la moneda encima y ajusta.";
      }

      // Si cambia referencia, obligamos a recalibrar
      pxPerMm = null;
      disableStep2();
      setStep(1);

      updateCalibShape();
      updateRing();
    }

    function computePxPerMm() {
      const ref = REFS[refSelect.value];
      const px = shape.getBoundingClientRect().width; // ancho real en pantalla
      pxPerMm = px / ref.mm;
    }

    /**
     * Ajusta el rango del slider del anillo a valores “realistas”
     * según la tabla de tallas Émolo y la escala pxPerMm.
     */
    function setRingSliderRange() {
      if (!pxPerMm) return;

      const minMm = Math.min(...EMOLO_SIZES.map(s => s.mm)) - 1.0;
      const maxMm = Math.max(...EMOLO_SIZES.map(s => s.mm)) + 1.0;

      ringSlider.min = String(Math.round(minMm * pxPerMm));
      ringSlider.max = String(Math.round(maxMm * pxPerMm));
      ringSlider.value = String(Math.round(((minMm + maxMm) / 2) * pxPerMm));
    }

    function formatBetween(a, b) {
      const tpl = i18n.between || "Estás entre {a} y {b}.";
      const note = i18n.note_between || "Recomendación: la mayor.";
      return (tpl.replace("{a}", a).replace("{b}", b) + " " + note).trim();
    }

    function formatEquiv(eu, us) {
      const tpl = i18n.equiv || "Equivalencia aprox.: EU {eu} · US {us}";
      return tpl.replace("{eu}", eu).replace("{us}", us);
    }

    function formatDiameter(mm) {
      // Puedes traducir "Ø {mm} mm" con i18n.diameter_value
      const tpl = i18n.diameter_value || "Ø {mm} mm (diámetro interior)";
      return tpl.replace("{mm}", mm.toFixed(1));
    }

    function updateRing() {
      const px = Number(ringSlider.value);
      ring.style.width = px + "px";
      ring.style.height = px + "px";

      if (!pxPerMm) {
        if (mmOut) mmOut.textContent = "— mm";
        if (sizeOut) sizeOut.textContent = "—";
        if (diaOut) diaOut.textContent = "—";
        if (equivOut) equivOut.textContent = i18n.calibrate_first || "Calibra primero.";
        return;
      }

      const dmm = px / pxPerMm; // diámetro interior en mm (estimado)

      if (mmOut) mmOut.textContent = dmm.toFixed(1) + " mm";

      const { best, between } = closestSize(dmm);

      if (sizeOut) sizeOut.textContent = best.size;

      // EXTRA: mostramos el diámetro objetivo de la talla recomendada (para generar confianza)
      if (diaOut) {
        const label = i18n.diameter_label || "";
        diaOut.textContent = (label ? label + " " : "") + formatDiameter(best.mm);
      }

      if (equivOut) {
        equivOut.textContent = between
          ? formatBetween(between.a.size, between.b.size)
          : formatEquiv(best.eu, best.us);
      }
    }

    // Eventos
    refSelect.addEventListener("change", setRefUI);
    calibSlider.addEventListener("input", updateCalibShape);
    ringSlider.addEventListener("input", updateRing);

    calibDone.addEventListener("click", () => {
      computePxPerMm();
      setRingSliderRange();
      enableStep2();
      setStep(2);
      updateRing();
    });

    // Init
    disableStep2();
    setRefUI();
    updateCalibShape();
    updateRing();
  }

  /** Inicializa todas las instancias presentes en la página */
  function boot() {
    document.querySelectorAll("[data-emolo-ring-sizer]").forEach(initRingSizer);
  }

  // Carga normal
  document.addEventListener("DOMContentLoaded", boot);

  // Shopify Theme Editor: cuando se recarga una sección
  document.addEventListener("shopify:section:load", function (e) {
    const section = e.target;
    if (!section) return;
    section.querySelectorAll("[data-emolo-ring-sizer]").forEach(root => {
      // permitir reinit si el DOM se recrea
      root.dataset.emoloInit = "0";
      initRingSizer(root);
    });
  });
})();